import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

// Unauthorized Access Page
const UnauthorizedAccess = ({ auth }) => {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      if (auth?.currentUser) {
        await auth.signOut();
      }
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    } catch (error) {
      console.error('Error during sign out:', error);
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-lg p-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <svg 
              className="w-12 h-12 text-yellow-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
        </div>
        
        <div className="text-center space-y-4">
          <p className="text-gray-600">
            Your account is not authorized to access this application.
          </p>
          <p className="text-gray-600">
            While you've successfully signed in, your email is not on the authorized users list.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Please contact your administrator to request access:
            <br />
            <a href="mailto:admin@example.com" className="text-blue-600 hover:underline">
              admin@example.com
            </a>
          </p>
        </div>

        <div className="mt-6 flex justify-center">
          <button 
            onClick={handleSignOut}
            className="w-full max-w-xs bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

// Authorization check hook
export const useAuthorizationCheck = (auth, db) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth?.currentUser) return;

    const checkAuthorization = async () => {
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          // First time user - try to create their document
          try {
            await setDoc(userRef, {
              email: auth.currentUser.email,
              createdAt: new Date()
            });
          } catch (error) {
            if (error.message?.includes('Unsupported field value: undefined')) {
              // This error indicates the user is not in the whitelist
              navigate('/unauthorized');
            }
            throw error;
          }
        }
      } catch (error) {
        console.error('Authorization check error:', error);
        navigate('/unauthorized');
      }
    };

    checkAuthorization();
  }, [auth?.currentUser, db, navigate]);
};

// Utility function to wrap Firebase operations with authorization check
export const withAuthorization = (operation) => async (auth, db, ...args) => {
  try {
    return await operation(...args);
  } catch (error) {
    if (error.message?.includes('Unsupported field value: undefined') && 
        error.message?.includes('users/')) {
      const navigate = useNavigate();
      navigate('/unauthorized');
      throw new Error('Unauthorized access');
    }
    throw error;
  }
};

export { UnauthorizedAccess };